/**
 * E-Signature Modal Component - Phase 2E
 * Reusable modal for document viewing and electronic signing
 */
import React, { useState } from 'react';
import { formatDateTimeLocale } from '../utils/dateFormat';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { 
  X, FileText, CheckCircle, AlertCircle, Clock,
  ChevronLeft, ChevronRight, Download, Maximize2,
  PenTool, Shield
} from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { Skeleton } from './ui/skeleton';
import { SafeHtml } from './security/SafeHtml';

const ESignatureModal = ({ 
  isOpen, 
  onClose, 
  document, 
  onSign,
  patientName = '' 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // CMS content for document body and agreement text
  const { loading: docLoading, content: docContent } = useContent('e_signature_document_body', { flow: 'document_signing' });
  const { content: agreementContent } = useContent('e_signature_agreement_text', { flow: 'document_signing' });

  if (!isOpen || !document) return null;

  const totalPages = 3; // Mock page count

  const handleSign = async () => {
    if (!hasAgreed || !typedSignature.trim()) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const signatureData = {
      typedName: typedSignature,
      agreed: hasAgreed,
      timestamp: new Date().toISOString()
    };
    
    await onSign(signatureData);
    
    setShowSuccess(true);
    
    setTimeout(() => {
      setShowSuccess(false);
      setHasAgreed(false);
      setTypedSignature('');
      setCurrentPage(1);
      onClose();
    }, 2000);
    
    setIsSubmitting(false);
  };

  const canSign = hasAgreed && typedSignature.trim().length >= 2;

  // Success overlay
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md mx-4 animate-in zoom-in-95">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Signed!</h2>
          <p className="text-gray-600">Your signature has been recorded successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-semibold">{document.title}</h2>
              <p className="text-sm text-slate-300">Version {document.currentVersion}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {document.isRequired && (
              <Badge className="bg-red-500/20 text-red-200 border-red-400/30">
                Required Document
              </Badge>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Document Preview */}
          <div className="flex-1 bg-slate-100 p-6 overflow-auto">
            <div className="bg-white rounded-lg shadow-lg max-w-3xl mx-auto min-h-[600px] p-8">
              <div className="space-y-6">
                <div className="text-center border-b pb-6">
                  <h1 className="text-2xl font-bold text-slate-800">{document.title}</h1>
                  <p className="text-sm text-slate-500 mt-2">Version {document.currentVersion} • Last Updated: {document.versions?.[0]?.uploadDate}</p>
                </div>

                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-600">{document.description}</p>
                  
                  {docLoading ? (
                    <div className="space-y-4 mt-6">
                      <Skeleton className="h-5 w-1/3" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                      <Skeleton className="h-5 w-1/3 mt-4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-4/5" />
                    </div>
                  ) : docContent ? (
                    <SafeHtml html={docContent.body} />
                  ) : (
                    <div className="flex items-center gap-2 p-3 mt-4 text-sm text-destructive bg-destructive/10 rounded-md">
                      <AlertCircle className="h-4 w-4" />
                      <span>Document content unavailable</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Page Navigation */}
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">Page {currentPage} of {totalPages}</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Signature Panel - P1-A Fix: Proper scrolling with sticky CTA */}
          <div className="w-96 bg-white border-l flex flex-col max-h-full">
            {/* Panel Header - Fixed */}
            <div className="flex items-center gap-2 p-6 pb-4 border-b bg-white">
              <PenTool className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-lg">Sign Document</h3>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-6">
              {/* Agreement Checkbox */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="agreement" 
                    checked={hasAgreed}
                    onCheckedChange={setHasAgreed}
                    className="mt-1"
                  />
                  <label htmlFor="agreement" className="text-sm text-slate-700 cursor-pointer">
                    {agreementContent?.body || 'I have read and agree to the terms above.'}
                  </label>
                </div>
              </div>

              {/* Signature Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Type Your Full Legal Name as Signature
                </label>
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder={patientName || "Enter your full name"}
                  className="text-lg font-serif italic h-12"
                  style={{ fontFamily: 'cursive, serif' }}
                />
                {typedSignature && (
                  <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg p-4 mt-2">
                    <p className="text-xs text-slate-500 mb-1">Signature Preview:</p>
                    <p className="text-2xl font-serif italic text-slate-800" style={{ fontFamily: 'cursive, serif' }}>
                      {typedSignature}
                    </p>
                  </div>
                )}
              </div>

              {/* Timestamp Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-700">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">Signature Timestamp</span>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  {formatDateTimeLocale(new Date())}
                </p>
              </div>

              {/* Security Badge */}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Shield className="h-4 w-4" />
                <span>Your signature is securely encrypted</span>
              </div>
            </div>

            {/* Sticky Action Buttons - P1-A Fix: Always visible at bottom */}
            <div className="sticky bottom-0 bg-white border-t p-6 space-y-3 shadow-lg">
              <Button 
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={handleSign}
                disabled={!canSign || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Confirm & Sign
                  </>
                )}
              </Button>
              <Button variant="outline" className="w-full" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ESignatureModal;
